// Message templates in a composer: "Start from a template" fills the
// subject, body, and files from a saved message, and "Save as template"
// saves the composer's message under a name.
//
// The server lists only templates of the composer's channel. The list
// loads once the composer opens, whenever its channel changes, and after
// a save, so the handlers listen on the control's container.
//
// Targets: [data-message-templates], whose value is the list's URL, and
// [data-message-template-form], the dialog that writes a template alone.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const LOAD_ERROR = "Couldn't load that template. Please try again.";

const LOCKED_ERROR =
  "This template comes with a higher plan. Upgrade to use it.";

const UPLOADING = "Wait for your files to finish uploading.";

const SAVE_ERROR = "Couldn't save the template. Please try again.";

// A refusal names the field at fault by the server's key, which for most
// fields is the field's name.
const REFUSED_FIELDS = {
  name: "[name=template_name]",
  body: "[data-message-surface]",
};

const SAVED_FIELDS = new Set([
  "template_name",
  "channel",
  "subject",
  "body",
  "attachment_ids",
]);

export function initMessageTemplates(root = document) {
  root.querySelectorAll("[data-message-templates]").forEach((box) => {
    if (!firstBind(box)) return;
    wire(box);
  });
  root.querySelectorAll("[data-message-template-form]").forEach((form) => {
    if (!firstBind(form)) return;
    wireForm(form);
  });
}

// The dialog moves focus to the field a refusal names, so the agent
// lands on the fix rather than on Save.
function wireForm(form) {
  clearOnEdit(form);
  form.addEventListener("htmx:afterSettle", (event) => {
    const refused = event.target.querySelector?.("[data-refused-field]");
    if (!refused) return;
    markRefused(form, refused.dataset.refusedField, event.target.id)?.focus();
  });
}

// One field per form reads as invalid at a time: the one the latest
// refusal named, until the agent edits it. It points at the refusal, so
// a screen reader names the reason again when the agent returns to it.
const marked = new WeakMap();

function markRefused(form, key, reasonID) {
  clearRefused(form);
  if (!key) return null;

  const field = form.querySelector(
    REFUSED_FIELDS[key] || `[name="${CSS.escape(key)}"]`,
  );
  if (!field) return null;
  field.setAttribute("aria-invalid", "true");
  field.setAttribute("aria-errormessage", reasonID);
  marked.set(form, field);

  return field;
}

function clearRefused(form) {
  const field = marked.get(form);
  field?.removeAttribute("aria-invalid");
  field?.removeAttribute("aria-errormessage");
  marked.delete(form);
}

function clearOnEdit(form) {
  form.addEventListener("input", (event) => {
    if (marked.get(form)?.contains(event.target)) clearRefused(form);
  });
}

function wire(box) {
  const form = box.closest("form");
  if (!form) return;

  const channel = () => form.querySelector("[name=channel]")?.value || "email";

  // Only the newest request of each kind counts. A channel change also
  // drops a template still loading, which was chosen for the old one.
  let loading = null;
  let listing = null;

  const list = async () => {
    listing?.abort();
    listing = new AbortController();
    loadError(box, "");
    const url = new URL(box.dataset.messageTemplates, location.origin);
    url.searchParams.set("channel", channel());
    try {
      const res = await fetch(url, {
        credentials: "same-origin",
        signal: listing.signal,
      });
      // An expired session redirects to the login page, which must never
      // land in the composer.
      if (!res.ok || res.redirected) {
        throw new Error(`template list returned ${res.status}`);
      }
      box.querySelector("[data-message-template-list]").outerHTML =
        await res.text();
    } catch (err) {
      if (err.name === "AbortError") return;
      // A list for the other channel would load the wrong kind of
      // message, so it goes.
      console.error("message template list failed", err);
      box.querySelector("[data-message-template-list]").replaceChildren();
    }
  };
  list();
  // The old channel's list goes at once, so nothing from it can load
  // into the new channel's composer.
  form.querySelector("select[name=channel]")?.addEventListener("change", () => {
    loading?.abort();
    box.querySelector("[data-message-template-list]").replaceChildren();
    list();
  });

  box.addEventListener("change", async (event) => {
    const select = event.target.closest("[data-message-template-select]");
    if (!select || !select.value) return;

    const id = select.value;
    // The list returns to its prompt, so choosing the same template again
    // reloads it.
    select.value = "";
    loading?.abort();
    loading = new AbortController();
    try {
      const res = await fetch(
        `/campaigns/messages/templates/${encodeURIComponent(id)}`,
        {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
          signal: loading.signal,
        },
      );
      // A premium template the plan lacks shows locked in the list, so
      // this answer means the plan changed since the list loaded.
      if (res.status === 402) {
        const refusal = await res.json().catch(() => ({}));
        loadError(box, refusal.error || LOCKED_ERROR);
        return;
      }
      if (!res.ok) throw new Error(`template returned ${res.status}`);
      apply(form, channel(), await res.json());
      loadError(box, "");
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("message template load failed", err);
      loadError(box, LOAD_ERROR);
    }
  });

  // Only a member who may create templates gets the save.
  const toggle = box.querySelector("[data-save-template-toggle]");
  if (!toggle) return;

  const row = box.querySelector("[data-save-template]");
  const name = box.querySelector("[data-save-template-name]");
  const save = box.querySelector("[data-save-template-submit]");
  const status = row.querySelector("[role=status]");
  toggle.addEventListener("click", () => {
    const open = row.hidden;
    row.hidden = !open;
    toggle.setAttribute("aria-expanded", String(open));
    if (open) {
      name.focus();
    } else {
      status.replaceChildren();
      clearRefused(form);
    }
  });
  // The field belongs to no form, so Enter saves the template instead of
  // doing nothing.
  name.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" || event.isComposing) return;
    event.preventDefault();
    save.click();
  });
  clearOnEdit(form);
  // A file still uploading posts no id, so the template would drop it.
  // A hidden picker, on a text, sends no files.
  save.addEventListener("htmx:beforeRequest", (event) => {
    const picker = form.querySelector("[data-attachment-picker]");
    if (!picker || picker.offsetParent === null) return;
    if (!picker.querySelector("[data-attachment-row][data-pending]")) return;
    event.preventDefault();
    status.textContent = UPLOADING;
  });
  // The save reads the message alone; a bulk composer's recipients and
  // filters stay behind.
  save.addEventListener("htmx:configRequest", (event) => {
    const params = event.detail.parameters;
    for (const key of [...params.keys()]) {
      if (!SAVED_FIELDS.has(key)) params.delete(key);
    }
  });
  // Focus stays in the save row, or moves to the field a refusal names,
  // even when the click never focused Save; the status line announces.
  status.addEventListener("htmx:afterSwap", (event) => {
    if (event.detail.xhr.status < 400) {
      name.value = "";
      clearRefused(form);
      list();
      name.focus();

      return;
    }

    const refused = status.querySelector("[data-refused-field]");
    // A failure with no refusal, such as a bare 500, still says so.
    if (!refused) status.textContent = SAVE_ERROR;
    const field = markRefused(form, refused?.dataset.refusedField, status.id);
    (field?.offsetParent ? field : name).focus();
  });
}

// apply fills the composer. A text template carries a body alone, so it
// leaves an email's subject and files, hidden on the text channel, as
// they were. The editor's own change stops an AI draft still streaming
// in.
function apply(form, channel, tpl) {
  form.querySelector("[data-message-surface]")?.dispatchEvent(
    new CustomEvent("message-editor:set-html", {
      detail: { html: tpl.body_html },
    }),
  );
  if (channel !== "email") return;

  const subject = form.querySelector("[name=subject]");
  if (subject) {
    subject.value = tpl.subject || "";
    subject.dispatchEvent(new Event("input", { bubbles: true }));
  }

  form.querySelector("[data-attachment-picker]")?.dispatchEvent(
    new CustomEvent("attachment-picker:set-files", {
      detail: { files: tpl.files || [] },
    }),
  );
}

function loadError(box, text) {
  const line = box.querySelector("[data-message-template-error]");
  line.textContent = text;
  line.hidden = text === "";
}
